import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const MobileContainer = styled.div`
  max-width: 100%;
`;

const TestResults = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #eee;
`;

const TestGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin: 1rem 0;
`;

const TestCard = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);

  h4 {
    margin: 0 0 0.5rem 0;
    color: #666;
    font-size: 0.9rem;
  }

  p {
    margin: 0;
    font-size: 1rem;
    color: ${props => props.status === 'good' ? '#28a745' : props.status === 'warning' ? '#ffc107' : '#dc3545'};
  }
`;

const TestButton = styled.button`
  padding: 0.5rem 1rem;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 1rem;
  margin-bottom: 1rem;

  &:hover {
    background: #357abd;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const IssueList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
`;

const IssueItem = styled.li`
  margin: 0.5rem 0;
  padding: 0.75rem;
  background: #fff;
  border-left: 4px solid ${props => props.severity === 'error' ? '#dc3545' : props.severity === 'warning' ? '#ffc107' : '#28a745'};
  border-radius: 4px;
  font-size: 0.9rem;
`;

const TestSection = styled.div`
  margin-bottom: 1.5rem;

  h3 {
    margin: 0 0 1rem 0;
    color: var(--primary-color);
    font-size: 1.1rem;
  }
`;

const DevicePreview = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);

  h4 {
    margin: 0 0 0.5rem 0;
    color: #666;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    color: #333;
  }
`;

const MobileTest = () => {
  const [testResults, setTestResults] = useState({
    viewport: {
      meta: null,
      width: null,
      height: null,
      scale: null
    },
    layout: {
      flexbox: null,
      grid: null,
      mediaQueries: null,
      overflow: null
    },
    typography: {
      fontSize: null,
      lineHeight: null,
      readability: null,
      contrast: null
    },
    touch: {
      targets: null,
      scrolling: null,
      gestures: null,
      keyboard: null
    }
  });

  const [issues, setIssues] = useState([]);
  const [deviceInfo, setDeviceInfo] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    pixelRatio: window.devicePixelRatio,
    orientation: window.screen.orientation.type
  });

  useEffect(() => {
    // Get device information
    const updateDeviceInfo = () => {
      setDeviceInfo({
        width: window.innerWidth,
        height: window.innerHeight,
        pixelRatio: window.devicePixelRatio,
        orientation: window.screen.orientation.type
      });
    };

    window.addEventListener('resize', updateDeviceInfo);
    window.addEventListener('orientationchange', updateDeviceInfo);

    return () => {
      window.removeEventListener('resize', updateDeviceInfo);
      window.removeEventListener('orientationchange', updateDeviceInfo);
    };
  }, []);

  const checkViewportSupport = () => {
    const results = {
      meta: {
        supported: !!document.querySelector('meta[name="viewport"]'),
        proper: document.querySelector('meta[name="viewport"]')?.content.includes('width=device-width')
      },
      width: {
        supported: true,
        value: window.innerWidth
      },
      height: {
        supported: true,
        value: window.innerHeight
      },
      scale: {
        supported: true,
        value: window.devicePixelRatio
      }
    };

    setTestResults(prev => ({ ...prev, viewport: results }));

    const newIssues = [];
    if (!results.meta.supported) {
      newIssues.push({ severity: 'error', message: 'Viewport meta tag missing' });
    } else if (!results.meta.proper) {
      newIssues.push({ severity: 'error', message: 'Viewport meta tag not properly configured' });
    }
    if (results.width.value < 320) {
      newIssues.push({ severity: 'warning', message: 'Viewport width below recommended minimum' });
    }
    if (results.height.value < 480) {
      newIssues.push({ severity: 'warning', message: 'Viewport height below recommended minimum' });
    }

    return newIssues;
  };

  const checkLayoutSupport = () => {
    const results = {
      flexbox: {
        supported: !!document.createElement('div').style.flex,
        proper: true
      },
      grid: {
        supported: !!document.createElement('div').style.grid,
        proper: true
      },
      mediaQueries: {
        supported: window.matchMedia('(max-width: 768px)').matches !== undefined,
        proper: true
      },
      overflow: {
        supported: true,
        proper: true
      }
    };

    setTestResults(prev => ({ ...prev, layout: results }));

    const newIssues = [];
    if (!results.flexbox.supported) {
      newIssues.push({ severity: 'error', message: 'Flexbox not supported' });
    }
    if (!results.grid.supported) {
      newIssues.push({ severity: 'warning', message: 'CSS Grid not supported' });
    }
    if (!results.mediaQueries.supported) {
      newIssues.push({ severity: 'error', message: 'Media queries not supported' });
    }

    return newIssues;
  };

  const checkTypographySupport = () => {
    const results = {
      fontSize: {
        supported: true,
        value: getComputedStyle(document.body).fontSize
      },
      lineHeight: {
        supported: true,
        value: getComputedStyle(document.body).lineHeight
      },
      readability: {
        supported: true,
        proper: true
      },
      contrast: {
        supported: true,
        proper: true
      }
    };

    setTestResults(prev => ({ ...prev, typography: results }));

    const newIssues = [];
    const fontSize = parseFloat(results.fontSize.value);
    if (fontSize < 12) {
      newIssues.push({ severity: 'error', message: 'Base font size too small for mobile' });
    }
    if (fontSize > 16) {
      newIssues.push({ severity: 'warning', message: 'Base font size may be too large for mobile' });
    }

    return newIssues;
  };

  const checkTouchSupport = () => {
    const results = {
      targets: {
        supported: true,
        proper: true
      },
      scrolling: {
        supported: true,
        proper: true
      },
      gestures: {
        supported: 'ontouchstart' in window,
        proper: true
      },
      keyboard: {
        supported: true,
        proper: true
      }
    };

    setTestResults(prev => ({ ...prev, touch: results }));

    const newIssues = [];
    if (!results.gestures.supported) {
      newIssues.push({ severity: 'warning', message: 'Touch gestures not supported' });
    }

    // Check for touch targets
    const touchTargets = document.querySelectorAll('button, a, input, select, textarea');
    const smallTargets = Array.from(touchTargets).filter(target => {
      const rect = target.getBoundingClientRect();
      return rect.width < 44 || rect.height < 44;
    });

    if (smallTargets.length > 0) {
      newIssues.push({ 
        severity: 'error', 
        message: `${smallTargets.length} touch targets below recommended size (44x44px)` 
      });
    }

    return newIssues;
  };

  const runAllTests = () => {
    setIssues([]);
    
    const viewportIssues = checkViewportSupport();
    const layoutIssues = checkLayoutSupport();
    const typographyIssues = checkTypographySupport();
    const touchIssues = checkTouchSupport();

    setIssues([
      ...viewportIssues,
      ...layoutIssues,
      ...typographyIssues,
      ...touchIssues
    ]);
  };

  const getStatusFromValue = (value) => {
    if (!value) return 'error';
    if (value.supported === false) return 'error';
    if (value.proper === false) return 'warning';
    return 'good';
  };

  return (
    <MobileContainer>
      <TestSection>
        <h3>Mobile Responsiveness Tests</h3>
        <TestButton onClick={runAllTests}>
          Run All Mobile Tests
        </TestButton>

        <DevicePreview>
          <h4>Current Device Information</h4>
          <p>Screen Width: {deviceInfo.width}px</p>
          <p>Screen Height: {deviceInfo.height}px</p>
          <p>Pixel Ratio: {deviceInfo.pixelRatio}</p>
          <p>Orientation: {deviceInfo.orientation}</p>
        </DevicePreview>

        <TestResults>
          <h3>Viewport Support</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue(testResults.viewport.meta)}>
              <h4>Viewport Meta</h4>
              <p>
                {testResults.viewport.meta ? 
                  `${testResults.viewport.meta.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.viewport.width)}>
              <h4>Viewport Width</h4>
              <p>
                {testResults.viewport.width ? 
                  `${testResults.viewport.width.value}px` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.viewport.height)}>
              <h4>Viewport Height</h4>
              <p>
                {testResults.viewport.height ? 
                  `${testResults.viewport.height.value}px` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.viewport.scale)}>
              <h4>Device Scale</h4>
              <p>
                {testResults.viewport.scale ? 
                  `${testResults.viewport.scale.value}x` : 
                  'Not tested'}
              </p>
            </TestCard>
          </TestGrid>

          <h3>Layout Support</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue(testResults.layout.flexbox)}>
              <h4>Flexbox</h4>
              <p>
                {testResults.layout.flexbox ? 
                  `${testResults.layout.flexbox.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.layout.grid)}>
              <h4>CSS Grid</h4>
              <p>
                {testResults.layout.grid ? 
                  `${testResults.layout.grid.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.layout.mediaQueries)}>
              <h4>Media Queries</h4>
              <p>
                {testResults.layout.mediaQueries ? 
                  `${testResults.layout.mediaQueries.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.layout.overflow)}>
              <h4>Overflow Handling</h4>
              <p>
                {testResults.layout.overflow ? 
                  `${testResults.layout.overflow.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
          </TestGrid>

          <h3>Typography Support</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue(testResults.typography.fontSize)}>
              <h4>Font Size</h4>
              <p>
                {testResults.typography.fontSize ? 
                  `${testResults.typography.fontSize.value}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.typography.lineHeight)}>
              <h4>Line Height</h4>
              <p>
                {testResults.typography.lineHeight ? 
                  `${testResults.typography.lineHeight.value}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.typography.readability)}>
              <h4>Readability</h4>
              <p>
                {testResults.typography.readability ? 
                  `${testResults.typography.readability.proper ? 'Good' : 'Poor'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.typography.contrast)}>
              <h4>Contrast</h4>
              <p>
                {testResults.typography.contrast ? 
                  `${testResults.typography.contrast.proper ? 'Good' : 'Poor'}` : 
                  'Not tested'}
              </p>
            </TestCard>
          </TestGrid>

          <h3>Touch Support</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue(testResults.touch.targets)}>
              <h4>Touch Targets</h4>
              <p>
                {testResults.touch.targets ? 
                  `${testResults.touch.targets.proper ? 'Good' : 'Poor'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.touch.scrolling)}>
              <h4>Scrolling</h4>
              <p>
                {testResults.touch.scrolling ? 
                  `${testResults.touch.scrolling.proper ? 'Good' : 'Poor'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.touch.gestures)}>
              <h4>Touch Gestures</h4>
              <p>
                {testResults.touch.gestures ? 
                  `${testResults.touch.gestures.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.touch.keyboard)}>
              <h4>Keyboard</h4>
              <p>
                {testResults.touch.keyboard ? 
                  `${testResults.touch.keyboard.proper ? 'Good' : 'Poor'}` : 
                  'Not tested'}
              </p>
            </TestCard>
          </TestGrid>

          {issues.length > 0 && (
            <>
              <h3>Mobile Responsiveness Issues</h3>
              <IssueList>
                {issues.map((issue, index) => (
                  <IssueItem key={index} severity={issue.severity}>
                    {issue.message}
                  </IssueItem>
                ))}
              </IssueList>
            </>
          )}
        </TestResults>
      </TestSection>
    </MobileContainer>
  );
};

export default MobileTest; 